package com.eladfinish.bible.contest.screens.pairMatching.model

// The topic models (PairsTopicItem etc.) live in :appContent, shared with the website generator.

interface UniqueIdentifiable {
    val id: Int
}

data class SortableData<T>(
    override val id: Int,
    val value: T,
) : UniqueIdentifiable

// ─── Game items ───────────────────────────────────────────────────────────────

/**
 * A single key item (left column, drop target).
 * [rawText] preserves asterisks for matching; [matchedValueRaw] is set on correct drop.
 */
data class KeyItem(
    override val id: Int,
    val rawText: String,
    val matchedValueRaw: String? = null,
) : UniqueIdentifiable {
    val isMatched: Boolean get() = matchedValueRaw != null
}

/**
 * A single value item (right column, draggable).
 * [isVisible] becomes false after a correct match so the slot stays empty.
 */
data class ValueItem(
    override val id: Int,
    val rawText: String,
    val isVisible: Boolean = true,
) : UniqueIdentifiable

// ─── Game phase ───────────────────────────────────────────────────────────────

sealed class GamePhase {
    /** No topic chosen yet (or after NewGame before dialog re-shows). */
    data object Idle : GamePhase()

    /** Two-column drag-and-drop game is active. */
    data class Playing(
        val topic: PairsTopicItem,
        val keyItems: List<KeyItem>,
        val valueItems: List<ValueItem>,
        val numberOfAttempts: Int = 0,
        val correctPairsSoFar: Int = 0,
        val solutionIsShown: Boolean = false,
    ) : GamePhase() {
        val numberOfPairs: Int get() = keyItems.size
        val isGameFinished: Boolean get() = correctPairsSoFar == numberOfPairs
        fun grade(): Int = if (numberOfAttempts == 0) 0
        else (100 * numberOfPairs.toDouble() / numberOfAttempts).toInt()
    }

    /** Sortable-list mode is active (keyTitle == SERIAL_NUMBER). */
    data class Sortable(
        val topicTitle: String,
        val columnHeader: String,
        val items: List<SortableData<String>>,
    ) : GamePhase()
}
